import {
	createContext,
	type Dispatch,
	type Provider,
	type SetStateAction,
	useContext,
	useMemo,
	useState,
} from 'react';

import type {
	ActivationCapability,
	Edition,
	PriceSegmentation,
	ProductId,
} from '../../../services/modal-data/types';
import type { CurrentUserProfile } from '../../../services/user-data';
import type { ModalState, ScreenState } from '../common/types';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';

/**
 * Context for sharing state and configuration across modal screens.
 * Contains only state/configuration fields.
 */
export interface ModalContextValue {
	autoStart: boolean;
	cloudId: string;
	currentScreen: ScreenState;
	currentUserProfile?: CurrentUserProfile;
	errorMessage: string;
	isNormalizeSeatsEnabled: boolean;
	locale: string;
	modalState: ModalState;
	orgId?: string;
	redirectUrl: string;
	requestedEditionId?: Edition;
	setCurrentScreen: Dispatch<SetStateAction<ScreenState>>;
	setErrorMessage: Dispatch<SetStateAction<string>>;
	setIsNormalizeSeatsEnabled: Dispatch<SetStateAction<boolean>>;
	setModalState: Dispatch<SetStateAction<ModalState>>;
	setRedirectUrl: Dispatch<SetStateAction<string>>;
	sourceComponent: string;
	sourceContext: string;
	touchpointName?: string;
	upgradeSourceProduct?: 'jira' | 'confluence';
}

const ModalContext = createContext<ModalContextValue | undefined>(undefined);

export const ModalProvider: Provider<ModalContextValue | undefined> = ModalContext.Provider;

export const useModalContext = (): ModalContextValue => {
	const ctx = useContext(ModalContext);
	if (!ctx) {
		throw new Error('useModalContext must be used within ModalProvider');
	}
	return ctx;
};

type ModalPresets = {
	initialIsNormalizeSeatsEnabled?: boolean;
	initialModalState?: Partial<ModalState>;
	initialRedirectUrl?: string;
	initialScreen?: ScreenState;
};

/**
 * Hook to create and manage modal state for the context provider.
 * Initializes all state values and returns a memoized context value.
 */
export const useModalState = (
	cloudId: string,
	sourceComponent: string,
	sourceContext: string,
	locale: string,
	orgId?: string,
	presets?: ModalPresets,
	autoStart: boolean = false,
	requestedEditionId?: Edition,
	attribution?: Pick<ModalContextValue, 'touchpointName' | 'upgradeSourceProduct'>,
): ModalContextValue => {
	const [currentScreen, setCurrentScreen] = useState<ScreenState>(
		presets?.initialScreen ?? 'loading',
	);
	const [redirectUrl, setRedirectUrl] = useState<string>(
		presets?.initialRedirectUrl ?? 'https://home.atlassian.com/',
	);
	const [modalState, setModalState] = useState<ModalState>(
		() =>
			({
				edition: 'premium' as Edition,
				hasPaymentDetails: true,
				userProducts: [] as ProductId[],
				// TODO(twcg-1572): prefetch path does not populate d0ActiveProducts
				d0ActiveProducts: [] as ProductId[],
				priceSegmentation: 'increase' as PriceSegmentation,
				priceValue: undefined as number | undefined,
				activationCapability: 'can-activate' as ActivationCapability,
				...presets?.initialModalState,
			}) as ModalState,
	);
	const [isNormalizeSeatsEnabled, setIsNormalizeSeatsEnabled] = useState<boolean>(
		presets?.initialIsNormalizeSeatsEnabled ?? true,
	);
	const [errorMessage, setErrorMessage] = useState<string>('');
	const currentUserProfile = useCurrentUserProfile();

	const contextValue = useMemo(
		() => ({
			touchpointName: attribution?.touchpointName,
			upgradeSourceProduct: attribution?.upgradeSourceProduct,
			autoStart,
			cloudId,
			locale,
			sourceComponent,
			sourceContext,
			currentScreen,
			setCurrentScreen,
			currentUserProfile,
			errorMessage,
			setErrorMessage,
			modalState,
			setModalState,
			orgId,
			requestedEditionId,
			redirectUrl,
			setRedirectUrl,
			isNormalizeSeatsEnabled,
			setIsNormalizeSeatsEnabled,
		}),
		[
			attribution?.touchpointName,
			attribution?.upgradeSourceProduct,
			autoStart,
			cloudId,
			locale,
			sourceComponent,
			sourceContext,
			currentScreen,
			currentUserProfile,
			errorMessage,
			modalState,
			orgId,
			requestedEditionId,
			redirectUrl,
			isNormalizeSeatsEnabled,
		],
	);

	return contextValue;
};
